import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SecurityShell, { cardStyle, pillStyle } from '../../components/security/SecurityShell';
import useSecurityFieldContext from '../../hooks/useSecurityFieldContext';
import { useTheme } from '../../theme/ThemeProvider';
import {
  doesCheckpointCodeMatch,
  getCheckpointStatusLabel,
  getCheckpoints,
  getCurrentCoords,
  getPatrolHealth,
  savePatrolScan,
} from '../../utils/securityFieldOps';

const DEMO_ROUTES = [
  {
    id: 'day-patrol',
    name: 'Day Patrol',
    description: 'Main daytime checkpoint route',
    checkpoints: [
      { id: 'gate-a', name: 'Gate A', description: 'Main access gate', order: 1, qrToken: 'QR-GATE-A' },
      { id: 'entrance', name: 'Entrance', description: 'Front entrance', order: 2, qrToken: 'QR-ENTRANCE' },
      { id: 'parking', name: 'Parking', description: 'Parking area sweep', order: 3, qrToken: 'QR-PARKING' },
    ],
  },
  {
    id: 'night-patrol',
    name: 'Night Patrol',
    description: 'Night perimeter route',
    checkpoints: [
      { id: 'north-perimeter', name: 'Perimeter North', description: 'North fence line', order: 1, qrToken: 'QR-NORTH' },
      { id: 'south-perimeter', name: 'Perimeter South', description: 'South fence line', order: 2, qrToken: 'QR-SOUTH' },
      { id: 'control-room', name: 'Control Room', description: 'Control room confirmation', order: 3, qrToken: 'QR-CONTROL' },
    ],
  },
];

function buildRoutes(checkpoints = []) {
  if (!checkpoints.length) return DEMO_ROUTES;

  const map = new Map();

  checkpoints.forEach((cp, index) => {
    const routeId = String(cp.routeId || cp.routeName || cp.route || 'default-route');
    const routeName = String(cp.routeName || cp.route || 'General Patrol');

    if (!map.has(routeId)) {
      map.set(routeId, {
        id: routeId,
        name: routeName,
        description: cp.routeDescription || '',
        checkpoints: [],
      });
    }

    map.get(routeId).checkpoints.push({
      ...cp,
      id: cp.id || `${routeId}-${index + 1}`,
      order: cp.order ?? cp.sequence ?? cp.position ?? index + 1,
      description: cp.description || cp.label || cp.qrToken || 'Checkpoint',
      qrToken: cp.qrToken || cp.token || cp.code || `${routeId}-${index + 1}`,
    });
  });

  return Array.from(map.values()).map((route) => ({
    ...route,
    checkpoints: [...route.checkpoints].sort((a, b) => Number(a.order || 0) - Number(b.order || 0)),
  }));
}

function getCheckpointLog(routeStatuses, checkpoint) {
  return (
    routeStatuses.get(String(checkpoint.id)) ||
    routeStatuses.get(String(checkpoint.name)) ||
    null
  );
}

export default function SecurityPatrolsPage() {
  const navigate = useNavigate();
  const { loading, orgId, activeShift, siteId, recentPatrols, refresh } = useSecurityFieldContext();
  const { themeMode, tokens } = useTheme();
  const ui = useMemo(() => getStyles(themeMode, tokens), [themeMode, tokens]);
  const shellCardStyle = useMemo(() => ({ ...cardStyle, ...ui.card }), [ui]);
  const shellPillStyle = useMemo(() => ({ ...pillStyle, ...ui.pill }), [ui]);

  const [siteCheckpoints, setSiteCheckpoints] = useState([]);
  const [message, setMessage] = useState('');
  const [busyId, setBusyId] = useState('');
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [skipTarget, setSkipTarget] = useState(null);
  const [skipReason, setSkipReason] = useState('');
  const [scanTarget, setScanTarget] = useState(null);
  const [scanCode, setScanCode] = useState('');
  const [scanError, setScanError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!orgId || !siteId) return;
      try {
        const rows = await getCheckpoints(orgId, siteId);
        if (!cancelled) {
          setSiteCheckpoints((rows || []).filter((row) => row.active !== false));
        }
      } catch (err) {
        if (!cancelled) {
          setSiteCheckpoints([]);
          setMessage(err?.message || 'Could not load patrol routes.');
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [orgId, siteId]);

  const routes = useMemo(() => buildRoutes(siteCheckpoints), [siteCheckpoints]);

  useEffect(() => {
    if (!selectedRouteId && routes.length) {
      setSelectedRouteId(routes[0].id);
    } else if (selectedRouteId && !routes.find((route) => route.id === selectedRouteId)) {
      setSelectedRouteId(routes[0]?.id || '');
    }
  }, [routes, selectedRouteId]);

  const selectedRoute = useMemo(
    () => routes.find((route) => route.id === selectedRouteId) || null,
    [routes, selectedRouteId]
  );

  const shiftPatrols = useMemo(() => {
    return (recentPatrols || []).filter((row) => {
      if (!activeShift?.id) return true;
      return String(row.shiftId || '') === String(activeShift.id);
    });
  }, [recentPatrols, activeShift]);

  const routeStatuses = useMemo(() => {
    const map = new Map();
    shiftPatrols.forEach((row) => {
      const key = String(row.checkpointId || row.checkpointName || '');
      if (!key || map.has(key)) return;
      map.set(key, row);
    });
    return map;
  }, [shiftPatrols]);

  const selectedRouteLogs = useMemo(() => {
    if (!selectedRoute) return [];
    return shiftPatrols.filter((row) => String(row.routeId || '') === String(selectedRoute.id));
  }, [shiftPatrols, selectedRoute]);

  const progress = useMemo(() => {
    const total = selectedRoute?.checkpoints?.length || 0;
    let completed = 0;
    let skipped = 0;
    let review = 0;

    (selectedRoute?.checkpoints || []).forEach((checkpoint) => {
      const log = getCheckpointLog(routeStatuses, checkpoint);
      const status = String(log?.status || '').toLowerCase();
      if (status === 'skipped') skipped += 1;
      else if (status === 'flagged' || status === 'invalid') review += 1;
      else if (status) completed += 1;
    });

    return {
      total,
      completed,
      skipped,
      review,
      remaining: Math.max(0, total - completed - skipped - review),
    };
  }, [selectedRoute, routeStatuses]);

  const nextCheckpoint = useMemo(() => {
    const checkpoints = selectedRoute?.checkpoints || [];
    return checkpoints.find((checkpoint) => !getCheckpointLog(routeStatuses, checkpoint)) || checkpoints[0] || null;
  }, [selectedRoute, routeStatuses]);

  const patrolHealth = useMemo(() => getPatrolHealth(selectedRouteLogs, selectedRoute), [selectedRouteLogs, selectedRoute]);

  const lastRouteLog = selectedRouteLogs[0] || null;
  const lastScannedAt = lastRouteLog?.scannedAtClient || null;

  async function submitCheckpoint(checkpoint, opts = {}) {
    if (!activeShift?.id) {
      setMessage('You must be clocked in before logging patrol checkpoints.');
      return;
    }

    setMessage('');
    setScanError('');
    setBusyId(String(checkpoint.id || checkpoint.name || 'checkpoint'));

    try {
      const coords = await getCurrentCoords();
      const patrol = await savePatrolScan({
        orgId,
        siteId: activeShift.siteId || siteId,
        checkpoint,
        shiftId: activeShift.id,
        coords,
        previousScanAt: localStorage.getItem('neoSecurityLastPatrolAt'),
        statusOverride: opts.statusOverride,
        flagReason: opts.flagReason,
        scannedCode: opts.scannedCode,
        routeId: selectedRoute?.id,
        routeName: selectedRoute?.name,
        expectedCheckpointId: nextCheckpoint?.id,
        previousCheckpointId: lastRouteLog?.checkpointId || '',
      });

      const statusLabel = getCheckpointStatusLabel(patrol?.status, patrol?.flagReason);
      if (patrol?.status === 'skipped') {
        setMessage(`Checkpoint skipped: ${checkpoint.name}. Reason saved.`);
      } else if (patrol?.status === 'valid') {
        setMessage(`Checkpoint verified: ${checkpoint.name}.`);
      } else {
        setMessage(`${checkpoint.name} logged with ${statusLabel.toLowerCase()} status.`);
      }

      setSkipTarget(null);
      setSkipReason('');
      setScanTarget(null);
      setScanCode('');
      await refresh();
    } catch (err) {
      const safeMessage = err?.message || 'Could not log checkpoint.';
      setMessage(safeMessage);
      setScanError(safeMessage);
    } finally {
      setBusyId('');
    }
  }

  function openSkipModal(checkpoint) {
    setSkipTarget(checkpoint);
    setSkipReason('');
    setMessage('');
    setScanTarget(null);
    setScanCode('');
    setScanError('');
  }

  function openScanModal(checkpoint) {
    setScanTarget(checkpoint);
    setScanCode('');
    setScanError('');
    setMessage('');
    setSkipTarget(null);
  }

  async function confirmSkip() {
    if (!skipTarget) return;
    if (!skipReason.trim()) {
      setMessage('A skip reason is required.');
      return;
    }
    await submitCheckpoint(skipTarget, {
      statusOverride: 'skipped',
      flagReason: skipReason.trim(),
      scannedCode: skipTarget.qrToken || skipTarget.id,
    });
  }

  async function confirmScan() {
    if (!scanTarget) return;
    const value = scanCode.trim();
    if (!value) {
      setScanError('Enter or paste the checkpoint code to verify this point.');
      return;
    }

    if (!doesCheckpointCodeMatch(scanTarget, value)) {
      setScanError('That code does not match this checkpoint.');
      return;
    }

    await submitCheckpoint(scanTarget, {
      statusOverride: 'valid',
      scannedCode: value,
    });
  }

  return (
    <SecurityShell
      title="Patrols"
      subtitle="Tap the next checkpoint fast, keep patrol timing visible, and make guard actions obvious on shared field devices."
      topAction={
        <button type="button" style={ui.backBtn} onClick={() => navigate('/security')}>
          ← Back to Neo
        </button>
      }
      aside={
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={shellCardStyle}>
            <div style={ui.sideKicker}>SHIFT STATUS</div>
            <div style={ui.sideMetric}>{activeShift ? 'Active' : 'Shift required'}</div>
            <div style={ui.sideCopy}>{activeShift?.siteName || 'Patrol routes require an active shift.'}</div>
            {!activeShift ? (
              <button type="button" style={{ ...ui.primaryBtn, marginTop: 16, width: '100%' }} onClick={() => navigate('/security/shift')}>
                Open shift control
              </button>
            ) : null}
          </div>

          <div style={shellCardStyle}>
            <div style={ui.sideKicker}>PATROL HEALTH</div>
            <div style={ui.sideMetric}>{patrolHealth.title}</div>
            <div style={ui.sideCopy}>{patrolHealth.copy}</div>
          </div>

          <div style={shellCardStyle}>
            <div style={ui.sideKicker}>PROGRESS</div>
            <div style={ui.sideMetric}>{progress.completed + progress.skipped + progress.review}/{progress.total}</div>
            <div style={ui.sideCopy}>
              {progress.completed} reached · {progress.skipped} skipped · {progress.review} review · {progress.remaining} remaining
            </div>
          </div>

          <div style={shellCardStyle}>
            <div style={ui.sideKicker}>SCAN READY</div>
            <div style={ui.sideMetric}>QR / NFC</div>
            <div style={ui.sideCopy}>
              Tap flow is live now. Checkpoints already carry scan tokens so we can plug in camera or NFC next without changing the patrol structure.
            </div>
          </div>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 16 }}>
        {loading ? <div style={shellCardStyle}>Loading patrol routes…</div> : null}

        {!activeShift?.id ? (
          <div style={{ ...shellCardStyle, borderColor: themeMode === 'light' ? 'rgba(214,69,69,.18)' : 'rgba(248,113,113,.22)' }}>
            <div style={ui.heading}>Start a shift first</div>
            <div style={ui.copy}>
              Guards should not enter patrols outside a live shift. This keeps route logs tied to the correct site, guard, and time window.
            </div>
          </div>
        ) : null}

        <div style={shellCardStyle}>
          <div style={ui.heading}>Select patrol route</div>
          <div style={ui.copy}>
            Guards stay in the main Neo flow. Choose a route once, then use the large next-checkpoint card below for fast patrol tapping.
          </div>

          <div style={ui.routeGrid}>
            {routes.map((route) => {
              const active = route.id === selectedRouteId;
              return (
                <button
                  key={route.id}
                  type="button"
                  style={active ? ui.routeBtnActive : ui.routeBtn}
                  onClick={() => setSelectedRouteId(route.id)}
                >
                  <div style={ui.routeName}>{route.name}</div>
                  <div style={ui.routeMeta}>
                    {route.checkpoints.length} checkpoints
                    {route.description ? ` · ${route.description}` : ''}
                  </div>
                </button>
              );
            })}
          </div>

          {!siteCheckpoints.length ? (
            <div style={ui.notice}>
              No site routes were found yet, so demo routes are showing for testing.
            </div>
          ) : null}
        </div>

        {selectedRoute && nextCheckpoint ? (
          <div style={ui.upNextCard}>
            <div>
              <div style={ui.sideKicker}>UP NEXT</div>
              <div style={ui.upNextTitle}>{nextCheckpoint.order}. {nextCheckpoint.name}</div>
              <div style={ui.copy}>{nextCheckpoint.description || 'Checkpoint'}</div>
            </div>

            <div style={ui.scanTokenBox}>
              <div style={ui.sideKicker}>Scan token</div>
              <div style={ui.scanToken}>{nextCheckpoint.qrToken || nextCheckpoint.id}</div>
            </div>

            <div style={ui.bigActions}>
              <button
                type="button"
                style={ui.bigPrimaryBtn}
                onClick={() => openScanModal(nextCheckpoint)}
                disabled={!activeShift?.id || busyId === String(nextCheckpoint.id)}
              >
                {busyId === String(nextCheckpoint.id) ? 'Verifying…' : 'Scan checkpoint'}
              </button>
              <button
                type="button"
                style={ui.bigGhostBtn}
                onClick={() => openSkipModal(nextCheckpoint)}
                disabled={!activeShift?.id || busyId === String(nextCheckpoint.id)}
              >
                Skip with reason
              </button>
            </div>
          </div>
        ) : null}

        {selectedRoute ? (
          <div style={shellCardStyle}>
            <div style={ui.heading}>{selectedRoute.name}</div>
            <div style={ui.copy}>
              Patrol proof should come from scanning the correct checkpoint code. Manual reach buttons were removed so guards cannot simply tap through the route while standing still.
            </div>

            <div style={ui.list}>
              {selectedRoute.checkpoints.map((checkpoint) => {
                const log = getCheckpointLog(routeStatuses, checkpoint);
                const statusLabel = getCheckpointStatusLabel(log?.status, log?.flagReason);
                const busy = busyId === String(checkpoint.id || checkpoint.name || 'checkpoint');
                const isNext = String(nextCheckpoint?.id || '') === String(checkpoint.id || '');

                return (
                  <div key={checkpoint.id || checkpoint.name} style={isNext ? ui.checkpointCardActive : ui.checkpointCard}>
                    <div>
                      <div style={ui.rowTitle}>
                        {checkpoint.order}. {checkpoint.name}
                      </div>
                      <div style={ui.rowMeta}>{checkpoint.description || 'Checkpoint'}</div>
                      <div style={ui.tokenLine}>Token: {checkpoint.qrToken || checkpoint.id}</div>
                    </div>

                    <div style={ui.checkpointRight}>
                      <span style={shellPillStyle}>{statusLabel}</span>
                      <div style={ui.actions}>
                        <button
                          type="button"
                          style={ui.primaryBtn}
                          onClick={() => openScanModal(checkpoint)}
                          disabled={busy || !activeShift?.id}
                        >
                          {busy ? 'Saving…' : 'Scan'}
                        </button>
                        <button
                          type="button"
                          style={ui.ghostBtn}
                          onClick={() => openSkipModal(checkpoint)}
                          disabled={busy || !activeShift?.id}
                        >
                          Skip
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {message ? <div style={ui.notice}>{message}</div> : null}
          </div>
        ) : null}

        <div style={shellCardStyle}>
          <div style={ui.heading}>Recent patrol logs</div>
          <div style={ui.list}>
            {(shiftPatrols || []).length ? (
              shiftPatrols.map((row) => (
                <div key={row.id} style={ui.row}>
                  <div>
                    <div style={ui.rowTitle}>{row.checkpointName || 'Checkpoint'}</div>
                    <div style={ui.rowMeta}>{row.flagReason || row.routeName || row.status || 'logged'}</div>
                  </div>
                  <span style={shellPillStyle}>{getCheckpointStatusLabel(row.status, row.flagReason)}</span>
                </div>
              ))
            ) : (
              <div style={ui.empty}>No patrols logged yet on this device.</div>
            )}
          </div>
        </div>
      </div>

      {skipTarget ? (
        <div style={ui.modalOverlay}>
          <div style={ui.modalCard}>
            <div style={ui.modalTitle}>Skip checkpoint</div>
            <div style={ui.modalCopy}>
              {skipTarget.name} can only be skipped with a reason.
            </div>
            <textarea
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
              placeholder="Enter reason"
              style={ui.textarea}
            />
            <div style={ui.modalActions}>
              <button type="button" style={ui.ghostBtn} onClick={() => setSkipTarget(null)}>
                Cancel
              </button>
              <button type="button" style={ui.primaryBtn} onClick={confirmSkip}>
                Confirm skip
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {scanTarget ? (
        <div style={ui.modalOverlay}>
          <div style={ui.modalCard}>
            <div style={ui.modalTitle}>Scan checkpoint</div>
            <div style={ui.modalCopy}>
              This version is ready for QR / NFC wiring. For now, enter or paste the checkpoint token below to verify the point before Neo logs it.
            </div>

            <div style={ui.scanAssistBox}>
              <div style={ui.sideKicker}>Expected checkpoint</div>
              <div style={ui.scanAssistTitle}>{scanTarget.name}</div>
              <div style={ui.scanAssistMeta}>Expected token: {scanTarget.qrToken || scanTarget.id}</div>
            </div>

            <input
              value={scanCode}
              onChange={(e) => setScanCode(e.target.value)}
              placeholder="Scan / paste checkpoint code"
              style={ui.textInput}
              autoFocus
            />

            {scanError ? <div style={ui.scanError}>{scanError}</div> : null}

            <div style={ui.modalActions}>
              <button type="button" style={ui.ghostBtn} onClick={() => setScanTarget(null)}>
                Cancel
              </button>
              <button type="button" style={ui.primaryBtn} onClick={confirmScan}>
                Verify checkpoint
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </SecurityShell>
  );
}

const baseStyles = {
  backBtn: {
    cursor: 'pointer',
    color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,.12)',
    background: 'rgba(255,255,255,.04)',
    padding: '12px 15px',
    borderRadius: 14,
    fontWeight: 800,
  },
  heading: { fontSize: 18, fontWeight: 900 },
  copy: { marginTop: 10, lineHeight: 1.7, opacity: 0.88 },
  actions: { display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 },
  primaryBtn: {
    cursor: 'pointer',
    color: '#03121c',
    background: 'linear-gradient(90deg, #10b981 0%, #38bdf8 100%)',
    padding: '12px 15px',
    borderRadius: 14,
    border: 0,
    fontWeight: 900,
  },
  ghostBtn: {
    cursor: 'pointer',
    color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,.12)',
    background: 'rgba(255,255,255,.04)',
    padding: '12px 15px',
    borderRadius: 14,
    fontWeight: 800,
  },
  bigPrimaryBtn: {
    cursor: 'pointer',
    color: '#03121c',
    background: 'linear-gradient(90deg, #22c55e 0%, #38bdf8 100%)',
    padding: '18px 22px',
    borderRadius: 18,
    border: 0,
    fontWeight: 900,
    fontSize: 18,
    minHeight: 60,
  },
  bigGhostBtn: {
    cursor: 'pointer',
    color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,.12)',
    background: 'rgba(255,255,255,.05)',
    padding: '18px 22px',
    borderRadius: 18,
    fontWeight: 900,
    fontSize: 18,
    minHeight: 60,
  },
  notice: {
    marginTop: 14,
    padding: 14,
    borderRadius: 16,
    background: 'rgba(56,189,248,.08)',
    border: '1px solid rgba(56,189,248,.18)',
    lineHeight: 1.6,
  },
  list: { display: 'grid', gap: 12, marginTop: 14 },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: 14,
    borderRadius: 16,
    background: 'rgba(255,255,255,.04)',
    border: '1px solid rgba(255,255,255,.08)',
  },
  checkpointCard: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 14,
    padding: 16,
    borderRadius: 18,
    background: 'rgba(255,255,255,.04)',
    border: '1px solid rgba(255,255,255,.08)',
  },
  checkpointCardActive: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 14,
    padding: 16,
    borderRadius: 18,
    background: 'rgba(56,189,248,.08)',
    border: '1px solid rgba(56,189,248,.24)',
  },
  checkpointRight: {
    display: 'grid',
    justifyItems: 'end',
    gap: 12,
    minWidth: 220,
  },
  rowTitle: { fontWeight: 800 },
  rowMeta: { marginTop: 6, fontSize: 13, opacity: 0.72, wordBreak: 'break-word' },
  tokenLine: { marginTop: 10, fontSize: 12, opacity: 0.78, letterSpacing: '.04em' },
  empty: { opacity: 0.8, lineHeight: 1.6 },
  sideKicker: { fontSize: 12, fontWeight: 900, letterSpacing: '.08em', opacity: 0.7 },
  sideMetric: { marginTop: 10, fontSize: 24, fontWeight: 900 },
  sideCopy: { marginTop: 8, lineHeight: 1.6, opacity: 0.82 },
  routeGrid: { display: 'grid', gap: 12, marginTop: 16 },
  routeBtn: {
    textAlign: 'left',
    padding: 16,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,.1)',
    background: 'rgba(255,255,255,.04)',
    color: '#e2e8f0',
    cursor: 'pointer',
  },
  routeBtnActive: {
    textAlign: 'left',
    padding: 16,
    borderRadius: 16,
    border: '1px solid rgba(56,189,248,.3)',
    background: 'rgba(56,189,248,.08)',
    color: '#e2e8f0',
    cursor: 'pointer',
  },
  routeName: { fontWeight: 900, fontSize: 16 },
  routeMeta: { marginTop: 6, fontSize: 13, opacity: 0.74 },
  upNextCard: {
    padding: 18,
    borderRadius: 22,
    border: '1px solid rgba(56,189,248,.24)',
    background: 'linear-gradient(180deg, rgba(8,39,76,.96), rgba(10,26,46,.96))',
    display: 'grid',
    gridTemplateColumns: '1.2fr auto',
    gap: 16,
    alignItems: 'start',
  },
  upNextTitle: { marginTop: 8, fontSize: 24, fontWeight: 900 },
  scanTokenBox: {
    minWidth: 160,
    padding: 14,
    borderRadius: 18,
    background: 'rgba(255,255,255,.05)',
    border: '1px solid rgba(255,255,255,.1)',
  },
  scanToken: { marginTop: 10, fontSize: 16, fontWeight: 900 },
  bigActions: {
    gridColumn: '1 / -1',
    display: 'grid',
    gridTemplateColumns: '1fr 280px',
    gap: 12,
    marginTop: 6,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(2,8,18,.62)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    zIndex: 60,
  },
  modalCard: {
    width: 'min(560px, calc(100vw - 32px))',
    borderRadius: 18,
    padding: 20,
    background: 'linear-gradient(180deg, rgba(20,40,68,0.98), rgba(12,30,54,0.98))',
    border: '1px solid rgba(123,186,255,0.22)',
    boxShadow: '0 22px 48px rgba(0,0,0,0.45)',
  },
  modalTitle: { fontSize: 22, fontWeight: 900 },
  modalCopy: { marginTop: 8, lineHeight: 1.6, opacity: 0.82 },
  textarea: {
    width: '100%',
    minHeight: 120,
    marginTop: 14,
    padding: '14px 16px',
    borderRadius: 16,
    background: 'rgba(255,255,255,.05)',
    color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,.1)',
    resize: 'vertical',
  },
  textInput: {
    width: '100%',
    marginTop: 14,
    padding: '16px 18px',
    borderRadius: 16,
    background: 'rgba(255,255,255,.05)',
    color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,.12)',
    outline: 'none',
    fontSize: 16,
  },
  scanAssistBox: {
    marginTop: 16,
    padding: 14,
    borderRadius: 16,
    background: 'rgba(255,255,255,.05)',
    border: '1px solid rgba(255,255,255,.1)',
  },
  scanAssistTitle: { marginTop: 8, fontSize: 18, fontWeight: 900 },
  scanAssistMeta: { marginTop: 8, opacity: 0.78 },
  scanError: {
    marginTop: 12,
    padding: 12,
    borderRadius: 14,
    background: 'rgba(248,113,113,.1)',
    border: '1px solid rgba(248,113,113,.2)',
    color: '#fecaca',
    lineHeight: 1.5,
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 14,
  },
};


function getStyles(themeMode, tokens = {}) {
  const isLight = themeMode === 'light';
  if (!isLight) {
    return {
      ...baseStyles,
      card: {},
      pill: {},
    };
  }

  return {
    ...baseStyles,
    card: {
      background: 'rgba(255,255,255,0.82)',
      border: '1px solid rgba(15, 23, 42, 0.08)',
      boxShadow: '0 18px 44px rgba(15, 23, 42, 0.06)',
    },
    pill: {
      background: 'rgba(47,128,237,.08)',
      border: '1px solid rgba(47,128,237,.12)',
      color: '#1f4f94',
    },
    backBtn: {
      ...baseStyles.backBtn,
      color: '#12335f',
      border: '1px solid rgba(15, 23, 42, 0.08)',
      background: 'rgba(255,255,255,0.76)',
      boxShadow: '0 8px 20px rgba(15,23,42,.06)',
    },
    heading: {
      ...baseStyles.heading,
      color: '#12335f',
    },
    copy: {
      ...baseStyles.copy,
      color: '#607897',
      opacity: 1,
    },
    primaryBtn: {
      ...baseStyles.primaryBtn,
      color: '#ffffff',
      background: 'linear-gradient(90deg, #2f80ed 0%, #4f9dff 100%)',
      boxShadow: '0 12px 24px rgba(47,128,237,.18)',
    },
    ghostBtn: {
      ...baseStyles.ghostBtn,
      color: '#12335f',
      border: '1px solid rgba(15, 23, 42, 0.08)',
      background: 'rgba(255,255,255,.72)',
    },
    bigPrimaryBtn: {
      ...baseStyles.bigPrimaryBtn,
      color: '#ffffff',
      background: 'linear-gradient(90deg, #2f80ed 0%, #4f9dff 100%)',
      boxShadow: '0 16px 28px rgba(47,128,237,.18)',
    },
    bigGhostBtn: {
      ...baseStyles.bigGhostBtn,
      color: '#12335f',
      border: '1px solid rgba(15, 23, 42, 0.08)',
      background: 'rgba(255,255,255,.72)',
    },
    notice: {
      ...baseStyles.notice,
      background: 'rgba(47,128,237,.08)',
      border: '1px solid rgba(47,128,237,.12)',
      color: '#335d8f',
    },
    row: {
      ...baseStyles.row,
      background: 'rgba(255,255,255,.76)',
      border: '1px solid rgba(15, 23, 42, 0.07)',
      boxShadow: '0 8px 18px rgba(15,23,42,.04)',
    },
    checkpointCard: {
      ...baseStyles.checkpointCard,
      background: 'rgba(255,255,255,.76)',
      border: '1px solid rgba(15, 23, 42, 0.07)',
      boxShadow: '0 8px 18px rgba(15,23,42,.04)',
    },
    checkpointCardActive: {
      ...baseStyles.checkpointCardActive,
      background: 'linear-gradient(180deg, rgba(255,255,255,.98), rgba(242,246,252,.98))',
      border: '1px solid rgba(47,128,237,.18)',
      boxShadow: '0 12px 28px rgba(47,128,237,.10)',
    },
    rowTitle: {
      ...baseStyles.rowTitle,
      color: '#12335f',
    },
    rowMeta: {
      ...baseStyles.rowMeta,
      color: '#6d83a2',
      opacity: 1,
    },
    tokenLine: {
      ...baseStyles.tokenLine,
      color: '#7c8faa',
      opacity: 1,
    },
    empty: {
      ...baseStyles.empty,
      color: '#6d83a2',
      opacity: 1,
    },
    sideKicker: {
      ...baseStyles.sideKicker,
      color: '#6d83a2',
      opacity: 1,
    },
    sideMetric: {
      ...baseStyles.sideMetric,
      color: '#12335f',
    },
    sideCopy: {
      ...baseStyles.sideCopy,
      color: '#607897',
      opacity: 1,
    },
    routeBtn: {
      ...baseStyles.routeBtn,
      border: '1px solid rgba(15, 23, 42, 0.08)',
      background: 'rgba(255,255,255,.72)',
      color: '#12335f',
      boxShadow: '0 8px 18px rgba(15,23,42,.04)',
    },
    routeBtnActive: {
      ...baseStyles.routeBtnActive,
      border: '1px solid rgba(47,128,237,.18)',
      background: 'linear-gradient(180deg, rgba(255,255,255,.98), rgba(242,246,252,.98))',
      color: '#12335f',
      boxShadow: '0 12px 24px rgba(47,128,237,.10)',
    },
    routeName: {
      ...baseStyles.routeName,
      color: '#12335f',
    },
    routeMeta: {
      ...baseStyles.routeMeta,
      color: '#6d83a2',
      opacity: 1,
    },
    upNextCard: {
      ...baseStyles.upNextCard,
      border: '1px solid rgba(47,128,237,.18)',
      background: 'linear-gradient(180deg, rgba(255,255,255,.98), rgba(242,246,252,.98))',
      boxShadow: '0 18px 36px rgba(47,128,237,.12)',
    },
    upNextTitle: {
      ...baseStyles.upNextTitle,
      color: '#12335f',
    },
    scanTokenBox: {
      ...baseStyles.scanTokenBox,
      background: 'rgba(47,128,237,.06)',
      border: '1px solid rgba(47,128,237,.12)',
    },
    scanToken: {
      ...baseStyles.scanToken,
      color: '#1f4f94',
    },
    modalCard: {
      ...baseStyles.modalCard,
      background: 'linear-gradient(180deg, rgba(255,255,255,.98), rgba(242,246,252,.98))',
      border: '1px solid rgba(15, 23, 42, 0.08)',
      boxShadow: '0 22px 48px rgba(15,23,42,.14)',
      color: '#12335f',
    },
    modalTitle: {
      ...baseStyles.modalTitle,
      color: '#12335f',
    },
    modalCopy: {
      ...baseStyles.modalCopy,
      color: '#607897',
      opacity: 1,
    },
    textarea: {
      ...baseStyles.textarea,
      background: '#fff',
      color: '#12335f',
      border: '1px solid rgba(15, 23, 42, 0.08)',
    },
    textInput: {
      ...baseStyles.textInput,
      background: '#fff',
      color: '#12335f',
      border: '1px solid rgba(15, 23, 42, 0.08)',
    },
    scanAssistBox: {
      ...baseStyles.scanAssistBox,
      background: 'rgba(47,128,237,.06)',
      border: '1px solid rgba(47,128,237,.12)',
    },
    scanAssistTitle: {
      ...baseStyles.scanAssistTitle,
      color: '#12335f',
    },
    scanAssistMeta: {
      ...baseStyles.scanAssistMeta,
      color: '#607897',
      opacity: 1,
    },
    scanError: {
      ...baseStyles.scanError,
      background: 'rgba(214,69,69,.08)',
      border: '1px solid rgba(214,69,69,.14)',
      color: '#b23b3b',
    },
  };
}
